import React, { useState, useContext } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { flowRight, get } from 'lodash'
import moment from 'moment'
import { Trans } from 'react-i18next'
import { withTranslationCustom } from 'lib/i18next'
import { withRouter } from 'react-router-dom'
import { EVENT_TYPE, getEventModalUrl } from 'lib/utils/store/event'
import { VEHICLE_STATUS } from 'lib/utils/store/vehicle'
import { EVENT_FORM_MODE } from 'components/Events/EventForm/EventForm'

import AppContext from 'components/AppContext'
import Button from 'components/UI/Button'
import Link from 'components/Link'
import { EventFormLogicContext } from 'components/Events/EventForm/EventFormLogic'
import VehicleProfileLogic from 'components/Vehicles/VehicleProfile/VehicleProfileLogic'

import './inspection-event-form-reminder-trafi-dialog.pcss'

const INVALID_DATA_ERROR = {
    NO_TRAFI_DATA: 'NO_TRAFI_DATA',
    NO_LAST_INSPECTION: 'NO_LAST_INSPECTION',
}

const InspectionEventFormCreateEventTrafiDialog = ({ t, location, wrappedVehicle, children }) => {
    const { writeEventFormState, writeEventFormInitialEventValues } = useContext(EventFormLogicContext)

    const lastTraficomFetchDate = wrappedVehicle.getLastTrafiDataFetchDate()
    const lastTraficomFetchDateFormatted = wrappedVehicle.getLastTrafiDataFetchDate('DD.MM.YYYY HH:mm')
    const trafiDataFetchedToday = lastTraficomFetchDate && moment(lastTraficomFetchDate).isSame(moment(Date.now()), 'day')
    const lastInspectionDate = wrappedVehicle.getVehicleLastInspection()
    const inspectionValidUntil = wrappedVehicle.getVehicleInspectionValidUntil()
    const inspectionValidUntilFormatted = wrappedVehicle.getVehicleInspectionValidUntil('DD.MM.YYYY')
    const vehicleInspectionIsValid = wrappedVehicle.vehicleInspectionIsValid()

    const [displayTrafiDialog, setDisplayTrafiDialog] = useState(true)
    const [invalidDataError, setInvalidDataError] = useState(null)
    const [allowFetch, setAllowFetch] = useState(!lastInspectionDate || !trafiDataFetchedToday)

    const displayFetchButton = allowFetch && !lastInspectionDate
    const displayRefreshButton = allowFetch && !!lastInspectionDate

    if (!displayTrafiDialog) {
        return children()
    }

    const handleFetchData = (requestTrafiData) => {
        return async () => {
            await requestTrafiData(wrappedVehicle.getId()).then((response) => {
                const trafiData = get(response, 'data.requestTrafiData.trafiData', null)
                setAllowFetch(false)

                if (!trafiData) {
                    setInvalidDataError(INVALID_DATA_ERROR.NO_TRAFI_DATA)
                } else if (!trafiData.vehicleDateOfLastInspection) {
                    setInvalidDataError(INVALID_DATA_ERROR.NO_LAST_INSPECTION)
                }
            }).catch(() => {
                setAllowFetch(false)
            })
        }
    }

    const renderWithoutTrafiData = () => {
        if (invalidDataError) return null

        return (
            <div className="inspection-event-form-reminder-trafi-dialog__trafi-description">
                <p>
                    {t('events:inspectionDialog.createEvent.withoutTrafiData.description')}
                </p>
            </div>
        )
    }

    const renderWithTrafiData = (vehicleProfileLogic) => {
        const { requestTrafiData } = vehicleProfileLogic
        return (
            <>
                <p>
                    {`${t('events:inspectionDialog.createEvent.withTrafiData.latestInspectionDateLabel')}: `}
                    <b className="nowrap">
                        <span>{wrappedVehicle.getVehicleLastInspection('DD.MM.YYYY')}</span>
                        {' / '}
                        <span>{wrappedVehicle.getVehicleLastInspectionStatusName(t)}</span>
                    </b>
                </p>
                <div className="inspection-event-form-reminder-trafi-dialog__trafi-description">
                    <p>
                        <Trans i18nKey="events:inspectionDialog.createEvent.withTrafiData.description" lastTraficomFetchDateFormatted={lastTraficomFetchDateFormatted}>
                            description <b className="nowrap">{{ lastTraficomFetchDateFormatted }}</b>
                        </Trans>
                        {' '}
                        {displayRefreshButton && (
                            <Button
                                onClick={handleFetchData(requestTrafiData)}
                                trackName="refreshTrafiData"
                                className="inline-button nowrap"
                            >
                                {t('events:inspectionDialog.createReminder.actions.refreshTrafiData')}
                            </Button>
                        )}
                    </p>

                    <div className="inspection-event-form-reminder-trafi-dialog__text">
                        {t('events:inspectionDialog.createEvent.withTrafiData.traficomText')}
                    </div>
                </div>
            </>
        )
    }

    const renderFetchButton = (vehicleProfileLogic) => {
        const { requestTrafiData, loading } = vehicleProfileLogic
        return (
            <Button
                disabled={wrappedVehicle.vehicle.status === VEHICLE_STATUS.OLD || wrappedVehicle.vehicle.status === VEHICLE_STATUS.SOLD}
                onClick={handleFetchData(requestTrafiData)}
                className="button button--secondary"
                trackName="fetchTrafiData"
                loading={loading}
            >
                {t('events:inspectionDialog.createEvent.actions.fetchTrafiData')}
            </Button>
        )
    }

    const renderUseDataButton = () => {
        return (
            <Button
                disabled={wrappedVehicle.vehicle.status === VEHICLE_STATUS.OLD || wrappedVehicle.vehicle.status === VEHICLE_STATUS.SOLD}
                onClick={() => {
                    writeEventFormInitialEventValues({ eventTime: lastInspectionDate })
                    if (vehicleInspectionIsValid) {
                        writeEventFormInitialEventValues({
                            reminders: {
                                reminderTime: inspectionValidUntil,
                            }
                        })
                    }
                    writeEventFormState({ trafiData: wrappedVehicle.getTrafiData() })
                    const status = wrappedVehicle.getVehicleLastInspectionStatus()
                    if (status) {
                        writeEventFormInitialEventValues({
                            eventData: {
                                trafiData: {
                                    inspectionResult: status,
                                    inspectionDate: lastInspectionDate,
                                }
                            }
                        })
                    }
                    setDisplayTrafiDialog(false)
                }}
                className="button button--secondary"
                trackName="useTrafiData"
            >
                {t('events:inspectionDialog.createEvent.actions.useTrafiData')}
            </Button>
        )
    }

    const renderSkipButton = () => {
        const noLastInspectionData = invalidDataError === INVALID_DATA_ERROR.NO_LAST_INSPECTION
        return (
            <Button
                onClick={() => setDisplayTrafiDialog(false)}
                className={`button ${noLastInspectionData ? 'button--secondary' : 'button--tertiary'}`}
                trackName="setManually"
            >
                {t(`events:inspectionDialog.createEvent.actions.${noLastInspectionData ? 'skipNoLastInspection' : 'skip'}`)}
            </Button>
        )
    }

    const displayUseTrafiDataButton = !!lastInspectionDate

    const renderActions = (vehicleProfileLogic) => {
        const { error } = vehicleProfileLogic

        if (invalidDataError === INVALID_DATA_ERROR.NO_LAST_INSPECTION) {
            const modalUrl = getEventModalUrl(location, EVENT_FORM_MODE.CREATE_REMINDER, wrappedVehicle.getId(), EVENT_TYPE.INSPECTION)
            return (
                <>
                    <Link
                        to={`${modalUrl}?use-trafi-data`}
                        className="button"
                        trackName="createReminder"
                    >
                        {t('events:inspectionDialog.createEvent.actions.createReminder')}
                    </Link>
                    {renderSkipButton()}
                </>
            )
        }

        if (error || invalidDataError) {
            return (
                <Button
                    onClick={() => setDisplayTrafiDialog(false)}
                    className="button"
                    trackName="onErrorContinue"
                >
                    {t('common:continue')}
                </Button>
            )
        }

        return (
            <>
                {displayFetchButton && renderFetchButton(vehicleProfileLogic)}
                {displayUseTrafiDataButton && renderUseDataButton()}
                {renderSkipButton()}
            </>
        )
    }

    const renderInvalidDateErrorMessage = () => {
        switch (invalidDataError) {
        case INVALID_DATA_ERROR.NO_LAST_INSPECTION:
            if (inspectionValidUntil) {
                return (
                    <p>
                        <Trans i18nKey="events:inspectionDialog.createEvent.noPreviousInspectionMessage" inspectionValidUntilFormatted={inspectionValidUntilFormatted}>
                            Vehicle has not been inspected yet. Inspect vehicle before: <b className={!vehicleInspectionIsValid ? 'alert-text' : ''}>{{ inspectionValidUntilFormatted }}</b>. Do you want to add reminder?
                        </Trans>
                    </p>
                )
            } else {
                return (
                    <p>
                        <Trans i18nKey="events:inspectionDialog.createEvent.noPreviousOrNextInspectionMessage">
                            Vehicle has not been inspected yet. Do you want to add reminder?
                        </Trans>
                    </p>
                )
            }
        case INVALID_DATA_ERROR.NO_TRAFI_DATA:
        default:
            return (
                <p className="alert-text">{t('events:inspectionDialog.fetchError')}</p>
            )
        }
    }

    return (
        <AppContext context="createEventTrafiDialog">
            <VehicleProfileLogic>
                {(vehicleProfileLogic) => (
                    <div className={classNames('inspection-event-form-reminder-trafi-dialog', { loading: vehicleProfileLogic.loading })}>
                        <h2>{t('events:INSPECTION.title')}</h2>
                        {lastInspectionDate ? (
                            renderWithTrafiData(vehicleProfileLogic)
                        ) : (
                            renderWithoutTrafiData(vehicleProfileLogic)
                        )}
                        {(vehicleProfileLogic.error || invalidDataError) && (
                            renderInvalidDateErrorMessage()
                        )}
                        <div className="action-wrapper action-wrapper--center action-wrapper--vertical">
                            {renderActions(vehicleProfileLogic)}
                        </div>
                    </div>
                )}
            </VehicleProfileLogic>
        </AppContext>
    )
}

InspectionEventFormCreateEventTrafiDialog.propTypes = {
    children: PropTypes.func.isRequired,
    wrappedVehicle: PropTypes.object.isRequired,
}

export default flowRight(
    withRouter,
    withTranslationCustom(['events', 'trafi']),
)(InspectionEventFormCreateEventTrafiDialog)
